/**
 * Plan Configuration
 * Centralized configuration for plan limits, thresholds, and pricing
 */

// ============================================================
// Plan Limits Configuration
// Used for filtering plans based on user's product count
// ============================================================
export const PLAN_LIMITS = {
  free: {
    conversationsPerMonth: 100,
    productsTraining: 100,
    chatHistoryDays: 30,
  },
  starter: {
    conversationsPerMonth: 1000,
    productsTraining: 500,
    chatHistoryDays: 90,
  },
  basic: {
    conversationsPerMonth: 5000,
    productsTraining: 4000,
    chatHistoryDays: 90,
  },
  // Note: 'startup' is the internal ID for 'Pro' plan
  startup: {
    conversationsPerMonth: 10000,
    productsTraining: 10000,
    chatHistoryDays: 90,
  },
  enterprise: {
    conversationsPerMonth: -1, // -1 means unlimited
    productsTraining: -1, // -1 means unlimited
    chatHistoryDays: -1, // -1 means unlimited
  },
  // Price Plan v2 — NEW cohort plans (unlimited everything)
  business: {
    conversationsPerMonth: -1,
    productsTraining: -1,
    chatHistoryDays: -1,
  },
  premium: {
    conversationsPerMonth: -1,
    productsTraining: -1,
    chatHistoryDays: -1,
  },
} as const;

// ============================================================
// Plan Pricing Configuration
// ============================================================
export const PLAN_PRICING = {
  free: {
    monthlyPrice: 0,
    yearlyPrice: 0,
  },
  starter: {
    monthlyPrice: 19,
    yearlyPrice: 14.25,
  },
  basic: {
    monthlyPrice: 49,
    yearlyPrice: 36.75,
  },
  startup: {
    monthlyPrice: 99,
    yearlyPrice: 74.25,
  },
  enterprise: {
    monthlyPrice: 199,
    yearlyPrice: 149,
  },
  // Price Plan v2 — NEW cohort (yearly = 25% off monthly)
  business: {
    monthlyPrice: 299,
    yearlyPrice: 224.25,
  },
  premium: {
    monthlyPrice: 599,
    yearlyPrice: 449.25,
  },
  // Digital Staff credit plans (Core / Growth) — yearly = 20% off monthly.
  ai_starter: {
    monthlyPrice: 199,
    yearlyPrice: 159.2,
  },
  ai_growth: {
    monthlyPrice: 299,
    yearlyPrice: 239.2,
  },
} as const;

// ============================================================
// Plan Credits (for billing/usage tracking)
// ============================================================
export const PLAN_CREDITS = {
  free: 100,
  starter: 1000,
  basic: 5000,
  startup: 10000,
  enterprise: 100000,
  business: -1,
  premium: -1,
  ai_starter: 4000,
  ai_growth: 7000,
} as const;

// ============================================================
// External URLs
// ============================================================
export const EXTERNAL_URLS = {
  enterpriseContact: 'https://calendly.com/joanna-chu-yepai/30min',
} as const;

// ============================================================
// Helper Functions
// ============================================================

/**
 * Get Enterprise contact URL with current month parameter
 */
export const getEnterpriseContactUrl = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${EXTERNAL_URLS.enterpriseContact}?month=${year}-${month}`;
};

/**
 * Format limit value for display
 * @param value - The limit value (-1 means unlimited)
 * @param unit - The unit to append (e.g., 'conversations / month')
 */
export const formatLimitText = (value: number, unit: string): string => {
  if (value === -1) {
    return `Unlimited ${unit}`;
  }
  return `Up to ${value} ${unit}`;
};

/**
 * Get user's plan product training limit based on payment status
 * @param paymentStatus - User's payment status
 * @returns Product training limit (-1 means unlimited, 0 means free plan limit)
 */
export const getUserPlanProductLimit = (paymentStatus: string | undefined | null): number => {
  if (!paymentStatus || paymentStatus === 'unpaid' || paymentStatus === 'expired') {
    return PLAN_LIMITS.free.productsTraining;
  }

  // Map payment status to plan ID
  const statusToPlanMap: Record<string, PlanId> = {
    basic: 'basic',
    basicTrial: 'basic',
    starter: 'starter',
    starterTrial: 'starter',
    startup: 'startup',
    startupTrial: 'startup',
    pro: 'enterprise',
    proTrial: 'enterprise',
    business: 'business',
    businessTrial: 'business',
    premium: 'premium',
    premiumTrial: 'premium',
  };

  const planId = statusToPlanMap[paymentStatus];
  if (!planId) {
    return PLAN_LIMITS.free.productsTraining;
  }

  return PLAN_LIMITS[planId].productsTraining;
};

// Type exports
export type PlanId = keyof typeof PLAN_LIMITS;
